/**
 * HUD ведения (спека 04). Экран телефона маленький, а карта — главное, поэтому
 * панель живёт в трёх детентах: скрыта (только ручка), одна строка (по
 * умолчанию) и подробности. Свайп вверх раскрывает, вниз — сворачивает; тап
 * делает то же одним касанием. Спрятать панель совсем можно только в полном
 * AR — в остальных режимах путь назад к маршруту обязан быть на экране
 * (Material 3 standard sheet, Apple Maps: панель ведения постоянна).
 */
import { formatDistance, formatDuration, tr } from '@app/i18n'
import { useEffect, useState } from 'preact/hooks'
import { advanceToPolylineIndex, stopFollow } from '../actions'
import { warmupAr } from '../ar/permissions'
import { importChunk } from '../lazy-retry'
import type { PositionSource } from '../nav'
import { cycleAlertProfile } from '../nav/alerts'
import { releaseCamera } from '../nav/camera-hold'
import {
  alertProfile,
  arMode,
  arSession,
  cameraHeld,
  followDetent,
  headingUpOn,
  langTick,
  navProgress,
  navSteps,
  routePlan,
  setGuidancePref,
  targetId,
  venue,
  venueMap,
} from '../state'
import { Glyph } from './glyph'
import {
  GLYPH_AR,
  GLYPH_CHECK,
  GLYPH_CHEVRON,
  GLYPH_COMPASS,
  GLYPH_FLAG,
  GLYPH_MUTE,
  GLYPH_SOUND,
  GLYPH_TURN_LEFT,
  GLYPH_TURN_RIGHT,
  GLYPH_VIBRATE,
} from './glyphs'
import { featureName } from './labels'
import { nextDetent, useDetentSwipe } from './use-detent'

/** Тогл профиля подсказок (№5): тихо → вибро → голос по кругу. */
function AlertToggle() {
  const profile = alertProfile.value
  const glyph =
    profile === 'voice' ? GLYPH_SOUND : profile === 'vibrate' ? GLYPH_VIBRATE : GLYPH_MUTE
  const label = tr(
    profile === 'voice'
      ? 'follow.alerts_voice'
      : profile === 'vibrate'
        ? 'follow.alerts_vibrate'
        : 'follow.alerts_silent',
  )
  return (
    <button
      type="button"
      class="btn-quiet alert-toggle"
      title={label}
      aria-label={label}
      onClick={() => {
        alertProfile.value = cycleAlertProfile(profile)
      }}
    >
      <Glyph svg={glyph} />
    </button>
  )
}

function SourceBadge({ source }: { readonly source: PositionSource }) {
  const key = source === 'gps' ? 'pos.gps' : source === 'anchor' ? 'pos.anchor' : 'pos.none'
  return <span class={`pos-badge pos-${source}`}>{tr(key)}</span>
}

/** «приход ≈ HH:MM» из оставшихся секунд (локальное время устройства). */
function arriveAt(remainingS: number): string {
  const t = new Date(Date.now() + remainingS * 1000)
  const hh = String(t.getHours()).padStart(2, '0')
  const mm = String(t.getMinutes()).padStart(2, '0')
  return `${hh}:${mm}`
}

/** Текущий шаг ведения без GPS: манёвр, остаток до него и переход к следующему. */
function useStepper() {
  const [stepIdx, setStepIdx] = useState(0)
  const steps = navSteps.value
  const plan = routePlan.value
  const isFinal = stepIdx >= steps.length
  const step = steps[stepIdx]
  const prevDoneM = stepIdx === 0 ? 0 : (steps[stepIdx - 1]?.distanceFromStartM ?? 0)
  const legM = Math.max(
    0,
    (isFinal ? (plan?.distanceM ?? 0) : (step?.distanceFromStartM ?? 0)) - prevDoneM,
  )
  const advance = (): void => {
    if (!plan) return
    const lastPolyIndex = plan.polyline.length - 1
    advanceToPolylineIndex(isFinal ? lastPolyIndex : (step?.index ?? lastPolyIndex), isFinal)
    setStepIdx(stepIdx + 1)
  }
  // Шага нет ровно тогда, когда он последний, — дальше только флаг цели.
  const turnGlyph = step ? (step.turn === 'left' ? GLYPH_TURN_LEFT : GLYPH_TURN_RIGHT) : GLYPH_FLAG
  const turnLabel = step
    ? tr(step.turn === 'left' ? 'follow.voice_left' : 'follow.voice_right')
    : ''
  return { stepIdx, total: steps.length + 1, legM, advance, turnGlyph, turnLabel }
}

type Stepper = ReturnType<typeof useStepper>

interface HudData {
  title: string
  /** Полная длина маршрута — для «пройдено» и доли прогресса. */
  totalM: number
  remainingM: number
  remainingS: number
  donePct: number
  source: PositionSource
  stepper: Stepper
}

/** Ручка скрытой панели: тонкая пилюля, тап или свайп вверх возвращают строку. */
function DetentGrip({
  onPointerDown,
  wasSwipe,
}: {
  onPointerDown: (e: PointerEvent) => void
  wasSwipe: () => boolean
}) {
  return (
    <button
      type="button"
      class="hud-grip"
      aria-label={tr('follow.show_panel')}
      onPointerDown={onPointerDown}
      onClick={() => {
        // Свайп уже сам решил, каким быть детенту — клик его не переигрывает.
        if (!wasSwipe()) followDetent.value = 'mini'
      }}
    >
      <Glyph svg={GLYPH_CHEVRON} />
    </button>
  )
}

/**
 * Свёрнутая строка — состояние по умолчанию: манёвр и дистанция до него, цель,
 * время; прогресс — волоском по верхней кромке. Без GPS справа кнопка «прошёл».
 */
function MiniBar({
  data,
  onPointerDown,
  wasSwipe,
}: {
  data: HudData
  onPointerDown: (e: PointerEvent) => void
  wasSwipe: () => boolean
}) {
  const { stepper, source } = data
  const gps = source === 'gps'
  const lead = gps ? data.remainingM : stepper.legM
  return (
    <div class="hud-mini" onPointerDown={onPointerDown}>
      <i class="hud-mini-progress" style={{ width: `${data.donePct}%` }} aria-hidden="true" />
      <button
        type="button"
        class="hud-mini-open"
        title={tr('follow.details')}
        onClick={() => {
          if (!wasSwipe()) followDetent.value = 'full'
        }}
      >
        <Glyph svg={stepper.turnGlyph} class="hud-mini-turn" />
        <span class="hud-mini-main">
          <b>{formatDistance(lead)}</b>
          <span class="hud-mini-target">{data.title}</span>
        </span>
        <span class="hud-mini-eta">{formatDuration(data.remainingS)}</span>
      </button>
      {!gps && (
        <button
          type="button"
          class="hud-mini-next"
          aria-label={tr('follow.next_step')}
          title={tr('follow.next_step')}
          onClick={() => {
            if (!wasSwipe()) stepper.advance()
          }}
        >
          <Glyph svg={GLYPH_CHECK} />
        </button>
      )}
    </div>
  )
}

/**
 * Способ движения (спека 13): AR-ведение и «карта по курсу». Живёт в
 * подробностях — в свёрнутой строке места нет, а во время AR способ
 * переключает сегмент-контрол поверх камеры.
 */
function GuidanceButtons() {
  const arOn = arSession.value === 'follow'
  const courseOn = headingUpOn.value
  const [note, setNote] = useState(false)
  useEffect(() => {
    if (!note) return
    const timer = setTimeout(() => setNote(false), 2600)
    return () => clearTimeout(timer)
  }, [note])

  const toggleAr = (): void => {
    if (arOn) {
      arSession.value = null
      setGuidancePref('map')
      return
    }
    setGuidancePref('ar')
    void (async () => {
      await warmupAr(true)
      await importChunk(() => import('../ar'))
      arSession.value = 'follow'
      followDetent.value = 'mini' // камере нужен экран целиком
    })()
  }
  const toggleCourse = (): void => {
    void (async () => {
      await warmupAr(false)
      const m = await importChunk(() => import('../ar'))
      if (courseOn) m.stopHeadingUp()
      else if (!(await m.startHeadingUp())) setNote(true) // датчиков нет — честно говорим
    })()
  }
  return (
    <>
      <div class="guide-row">
        <button type="button" class={arOn ? 'guide-btn guide-on' : 'guide-btn'} onClick={toggleAr}>
          <Glyph svg={GLYPH_AR} />
          {tr('ar.choice_ar')}
        </button>
        {!arOn && (
          <button
            type="button"
            class={courseOn ? 'guide-btn guide-on' : 'guide-btn'}
            onClick={toggleCourse}
          >
            <Glyph svg={GLYPH_COMPASS} />
            {tr('ar.course_btn')}
          </button>
        )}
      </div>
      {note && <p class="orient-note orient-note-warn">{tr('ar.sensors_denied')}</p>}
    </>
  )
}

/** Подробности: путь, честный источник позиции, способ движения, выход. */
function ExpandedSheet({
  data,
  onPointerDown,
  wasSwipe,
}: {
  data: HudData
  onPointerDown: (e: PointerEvent) => void
  wasSwipe: () => boolean
}) {
  const { stepper, source } = data
  return (
    <section class="sheet hud">
      <button
        type="button"
        class="hud-handle"
        aria-label={tr('follow.collapse')}
        onPointerDown={onPointerDown}
        onClick={() => {
          if (!wasSwipe()) followDetent.value = 'mini'
        }}
      >
        <i aria-hidden="true" />
      </button>
      <div class="sheet-head">
        <div>
          {/* Прибытие — час на часах: «во сколько буду» понятнее «сколько
              осталось», а остаток стоит строкой ниже (ScreenGuide). */}
          <div class="rp-arrival-label">{tr('follow.arrival')}</div>
          <div class="hud-arrival tabular weight-breathe">{arriveAt(data.remainingS)}</div>
          <div class="hud-remaining">
            {tr('route.remaining')} {formatDuration(data.remainingS)}
          </div>
          <p class="sheet-sub">{data.title}</p>
        </div>
        <div class="hud-head-side">
          <SourceBadge source={source} />
          <AlertToggle />
        </div>
      </div>
      {/* Прогресс с «держателем»: точка на конце пройденного показывает, где
          человек на пути, а не только сколько закрашено. */}
      <div class="progress">
        <i style={{ width: `${data.donePct}%` }} />
        <span class="progress-knob" style={{ left: `${data.donePct}%` }} aria-hidden="true" />
      </div>
      <div class="hud-stats">
        <div class="tc-stat">
          <div class="tc-stat-label">{tr('follow.stat_done')}</div>
          <div class="tc-stat-value tabular">
            {formatDistance(Math.max(0, data.totalM - data.remainingM))}
          </div>
        </div>
        <div class="tc-stat">
          <div class="tc-stat-label">{tr('follow.stat_left')}</div>
          <div class="tc-stat-value tabular">{formatDistance(data.remainingM)}</div>
        </div>
        <div class="tc-stat">
          <div class="tc-stat-label">{tr('follow.stat_steps')}</div>
          <div class="tc-stat-value tabular">
            {Math.min(stepper.stepIdx + 1, stepper.total)}/{stepper.total}
          </div>
        </div>
      </div>
      {source !== 'gps' && (
        <div class="stepper">
          <p class="stepper-label">
            {tr('follow.step_of', {
              k: String(Math.min(stepper.stepIdx + 1, stepper.total)),
              n: String(stepper.total),
            })}
            {' · '}
            <Glyph svg={stepper.turnGlyph} class="stepper-turn" />
            {stepper.turnLabel ? ` ${stepper.turnLabel} · ` : ' '}
            {formatDistance(stepper.legM)}
          </p>
          <button type="button" class="btn-primary btn-go" onClick={stepper.advance}>
            {tr('follow.next_step')}
          </button>
        </div>
      )}
      <GuidanceButtons />
      <button type="button" class="btn-quiet" onClick={stopFollow}>
        {tr('follow.exit')}
      </button>
    </section>
  )
}

/**
 * Кнопка «Вернуться к себе» — единственный видимый выход из удержания камеры.
 * Появляется, только когда человек отодвинул карту рукой: висеть постоянно ей
 * незачем, а прятать возврат в жест — значит оставить человека без своей
 * позиции (nav/camera-hold.ts).
 */
function RecenterButton() {
  if (!cameraHeld.value) return null
  return (
    <button
      type="button"
      class="map-recenter glass"
      onClick={() => {
        releaseCamera()
        const p = navProgress.peek()
        if (p?.position) venueMap.peek()?.followCamera(p.position, p.headingDeg)
      }}
    >
      <Glyph svg={GLYPH_COMPASS} />
      {tr('follow.recenter')}
    </button>
  )
}

/** HUD ведения: детент решает, сколько места панель занимает на экране. */
export function FollowHud() {
  langTick.value
  const progress = navProgress.value
  const plan = routePlan.value
  const v = venue.value
  const stepper = useStepper()
  const detent = followDetent.value
  // Прятать панель совсем разрешаем только в полном AR: в остальных режимах
  // человек обязан видеть путь назад к маршруту (Material 3 standard sheet).
  const fullAr = arMode.value === 'ar'
  const swipe = useDetentSwipe(
    () => {
      followDetent.value = nextDetent(followDetent.value, 'up')
    },
    () => {
      followDetent.value = nextDetent(followDetent.value, 'down', fullAr)
    },
  )
  // Камера заняла экран — строка ведения уходит в ручку (манёвр и так на
  // камере, дублировать его текстом незачем); телефон опущен — строка вернулась.
  useEffect(() => {
    if (fullAr && followDetent.peek() === 'mini') followDetent.value = 'hidden'
    if (!fullAr && followDetent.peek() === 'hidden') followDetent.value = 'mini'
  }, [fullAr])
  if (!plan || !v) return null
  const remainingM = progress?.remainingM ?? plan.distanceM
  const remainingS = progress?.remainingS ?? plan.durationS
  const id = targetId.value
  const data: HudData = {
    title: id ? featureName(v, id) : tr('follow.back_route'),
    totalM: plan.distanceM,
    remainingM,
    remainingS,
    donePct: plan.distanceM > 0 ? Math.min(100, (1 - remainingM / plan.distanceM) * 100) : 0,
    source: progress?.positionSource ?? 'none',
    stepper,
  }

  return (
    <>
      <RecenterButton />
      {detent === 'hidden' ? (
        <DetentGrip onPointerDown={swipe.onPointerDown} wasSwipe={swipe.wasSwipe} />
      ) : detent === 'mini' ? (
        <MiniBar data={data} onPointerDown={swipe.onPointerDown} wasSwipe={swipe.wasSwipe} />
      ) : (
        <ExpandedSheet data={data} onPointerDown={swipe.onPointerDown} wasSwipe={swipe.wasSwipe} />
      )}
    </>
  )
}
